import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import * as React from "react";
import { LuCircle, LuTrendingDown, LuTrendingUp } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/lib";

// Define the type for the props
export interface MetricProps {
  state: "active" | "inActive" | "none";
  className?: string;
  value: ReactNode;
  label: string;
  caption?: ReactNode;
  tooltip?: string;
  valueClassName?: string;
  icon?: ReactNode;
  trending?: "up" | "down" | "none" | false;
  color?: string;
  loading?: boolean;
}

const metricVariants = cva("relative cursor-auto rounded-xl", {
  variants: {
    state: {
      active: "border border-solid border-primary bg-transparent text-muted",
      inActive: `
        border border-solid border-border-light bg-transparent
        text-muted-foreground
        hover:border hover:border-border hover:bg-background
      `,
      none: `
        cursor-auto border border-solid border-transparent bg-card
        hover:border hover:border-border hover:bg-background
      `,
    },
  },
  defaultVariants: {
    state: "none",
  },
});

export const Metric = ({
  state,
  className,
  value,
  label,
  caption,
  icon,
  tooltip,
  color,
  valueClassName,
  trending = false,
  loading = false,
}: MetricProps) => {
  return (
    <div className={cn(metricVariants({ state, className }))}>
      <div className="p-4 pb-3">
        {tooltip && (
          <div className="absolute top-[10px] right-[10px]">
            <InfoTooltip text={tooltip} />
          </div>
        )}
        {icon && <div className="mb-3 flex w-full items-end">{icon}</div>}
        <div className="mb-2 flex w-full items-center gap-2 text-xs">
          {color && (
            <figure className="w-fit">
              <LuCircle
                className={`
                  size-3 fill-current
                  ${color}
                `}
              />
            </figure>
          )}
          <p>{label}</p>
        </div>
        <div
          className={cn(
            `
              mb-0 text-xl text-muted-foreground
              md:text-2xl
            `,
            valueClassName,
          )}
        >
          {!loading && <div>{value}</div>}
          {loading && <Skeleton className="h-[32px] w-[100px] bg-accent" />}
          {caption && (
            <div className="inline-block text-xs text-muted">{caption}</div>
          )}
        </div>
      </div>
      {trending && (
        <div className="mx-2 border-t border-border-light p-2 font-mono">
          {trending == "up" ? (
            <div className="relative">
              <span
                className="
                  flex items-center text-xs text-emerald-400
                  dark:text-green-500
                "
              >
                <LuTrendingUp className="mr-2 size-4" />
                9.3k (+31%) 30d
              </span>
            </div>
          ) : trending == "down" ? (
            <div className="relative">
              <span
                className="
                  flex items-center text-xs text-red-500
                  dark:text-red-400/80
                "
              >
                <LuTrendingDown className="mr-2 size-4" />
                182 (-29%) 30d
              </span>
            </div>
          ) : trending == "none" ? (
            <div className="relative">
              <span className="flex items-center text-xs text-muted">
                No Activity
              </span>
            </div>
          ) : (
            ""
          )}
        </div>
      )}
    </div>
  );
};
